import React, { useMemo } from 'react';
import { type PlaceholderReference } from '../../inputs/rich-text-advanced/menu-bar/placeholder/types.ts';
import { type IReference } from '../../schema/expressions/references/IReference.ts';
import {
  resolveOtherRootReferences,
  resolveTreeReferencesAndPath,
} from '../../schema/expressions/references/resolve/resolveTreeReferences.ts';
import { type IRootWidget } from '../../schema/widgets/repository/containers/root/IRootWidget.ts';
import { type Widget } from '../../schema/widgets/Widget.ts';
import {
  type IRootWidgetIdentifier,
  type PlaceholderParameterInputRenderFunction,
  useTreeReferencesGlobalContext,
} from './TreeReferencesGlobalContext.tsx';

export interface ITreeReferencesContext {
  /** Tutte le radici disponibili */
  allRoots: IRootWidgetIdentifier[];
  /** Nodo radice del Widget corrente */
  root?: IRootWidget;
  selectedRootId?: string;
  /** Widget corrente */
  widget?: Widget;
  /**
   * Riferimenti ad _altri_ widget disponibili a partire dal Widget corrente.
   * Sarebbe lo "scope"
   */
  references: IReference[];
  /** Riferimenti placeholder */
  placeholderReferences: PlaceholderReference[];
  /** Funzione per renderizzare l'input di un parametro placeholder */
  placeholderParameterInputRenderer?: PlaceholderParameterInputRenderFunction;
}

export const TreeReferencesContext =
  React.createContext<ITreeReferencesContext | null>(null);

interface ITreeReferencesContextProviderProps {
  root?: IRootWidget;
  // TODO Al momento utilizziamo il `selectedRootId` passato dal contesto globale,
  // questo non viene utilizzato.
  selectedRootId?: string;
  widget?: Widget;
  children: React.ReactNode;
}

/**
 * Definisce per tutti i children un contesto delle espressioni che espone
 * a tutti gli input annidati le informazioni sul Widget corrente.
 * Da utilizzare sopra a un Form di configurazione del Widget, ecc.
 */
export function TreeReferencesContextProvider(
  props: ITreeReferencesContextProviderProps,
) {
  const { root, widget, selectedRootId } = props;

  const {
    allRoots,
    selectedRootId: selectedRootIdFromParent,
    otherReferences,
    placeholderReferences = [],
    placeholderParameterInputRenderer,
  } = useTreeReferencesGlobalContext() ?? {
    allRoots: [],
    selectedRootId: undefined,
    otherReferences: [],
    placeholderReferences: [],
    placeholderParameterInputRenderer: undefined,
  };

  const value = useMemo(() => {
    const { path, references } =
      widget && root
        ? resolveTreeReferencesAndPath(root, widget)
        : { path: [], references: [] };

    // TODO Al momento utilizziamo il `selectedRootId` passato dal contesto
    // globale perché non ce l'abbiamo nel contesto locale. In futuro dovremmo
    // unificare i context sparsi che ci sono e passare il `selectedRootId`
    // insieme al `root` e al `widget` corrente.
    const otherRootsReferences = allRoots
      .filter(r => r.id !== selectedRootIdFromParent)
      .flatMap(r => resolveOtherRootReferences(r.id, r.root, path));

    references.push(...otherRootsReferences);

    references.push(...otherReferences);

    return {
      allRoots,
      root,
      widget,
      references,
      selectedRootId,
      placeholderReferences,
      placeholderParameterInputRenderer,
    };
  }, [
    widget,
    root,
    allRoots,
    otherReferences,
    selectedRootId,
    selectedRootIdFromParent,
    placeholderReferences,
    placeholderParameterInputRenderer,
  ]);

  return (
    <TreeReferencesContext.Provider value={value}>
      {props.children}
    </TreeReferencesContext.Provider>
  );
}

/**
 * Ritorna il contesto di modifica delle espressioni corrente. Un contesto
 * delle espressioni fa sempre riferimento a un **singolo widget**.
 */
export function useTreeReferencesContext(): ITreeReferencesContext {
  const context = React.useContext(TreeReferencesContext);
  if (context == null) {
    // biome-ignore format: <tbd>
    throw new Error('useTreeReferencesContext must be used within an TreeReferencesContext');
  }
  return context;
}
